/**
 * What this app keeps on the handset, for how long, and what removes it.
 *
 * docs/14 asks for this to be explicit per resource class rather than implied
 * by a caching library's defaults, and this file is that table as code — so a
 * new screen has to make a decision instead of inheriting one.
 *
 * ── One deliberate departure from docs/14 ──────────────────────────
 *
 * The document describes certificates and reports as stale-while-revalidate,
 * which is a Cache Storage strategy, "→ IndexedDB". That would put medical
 * JSON in two storage systems with different eviction rules and no shared
 * expiry, and "purged on logout" would then be a claim about both.
 *
 * So: **the service worker caches the application shell and static assets, and
 * nothing else. No API response ever enters Cache Storage.** Everything
 * sensitive lives here, in one IndexedDB database, with one wipe that provably
 * empties it. The offline behaviour docs/14 wants is unchanged; where it lives
 * is narrower, and the security claim is checkable rather than distributed.
 *
 * ── At rest ────────────────────────────────────────────────────────
 *
 * IndexedDB is not encrypted. On a handset, at-rest protection is the device
 * screen lock, and docs/14 states that plainly rather than pretending
 * otherwise. That is the reason the list below is short: everything here had
 * to earn its place by being useless to a worker without it.
 */

/** Bumping this discards every cached entry on next open. */
export const CACHE_SCHEMA_VERSION = 1;

const DAY = 24 * 60 * 60 * 1000;

/**
 * Wipes the whole store after this long without the app being opened.
 *
 * "Cleared on logout" is not a policy when a worker never logs out — they open
 * this app three to five times a year and close it. This is the clock that
 * actually runs.
 */
export const IDLE_WIPE_MS = 14 * DAY;

export interface CacheRule {
  /** Storage key, namespaced per employee by the store. */
  readonly key: string;
  /** How long a copy stays usable offline. */
  readonly ttlMs: number;
  /** Why this is on the device at all. Read before adding a row. */
  readonly because: string;
}

/**
 * The cacheable set. Anything not named here is network-only.
 *
 * Notably absent, on purpose:
 *
 *   · the consent notice and the consent submit. Consent must be given
 *     against the live template with a server timestamp; a notice read from a
 *     cache could be a retired version, and that is [D28]'s whole question.
 *   · notifications and the unread count. They change constantly and are
 *     worthless stale — a badge from last week is a lie, not a fallback.
 *   · anything unreleased. It never leaves the server in the first place.
 */
export const CACHE_RULES = {
  /**
   * The reason offline exists. A worker at a factory gate with no signal has
   * to be able to show that they are cleared to work.
   */
  certificates: {
    key: 'certificates',
    ttlMs: 30 * DAY,
    because: 'Shown at a gate, where there is often no signal. The longest life here.',
  },

  /**
   * Seven days, because nothing depends on holding them. A worker who wants a
   * report from last year can fetch it when they have signal.
   */
  reports: {
    key: 'reports',
    ttlMs: 7 * DAY,
    because: 'Useful to re-read after a checkup; nothing breaks without them.',
  },

  /**
   * Where and when to turn up — the thing worth having on a bus with no
   * signal. The store shortens this further to the appointment itself plus a
   * day: an address for a slot that has passed is clutter, not a fallback.
   */
  appointments: {
    key: 'appointments',
    ttlMs: 7 * DAY,
    because: 'Date, time and venue, needed on the way there.',
  },

  /** Journey status. Stale is survivable; blank is not. */
  examinations: {
    key: 'examinations',
    ttlMs: 7 * DAY,
    because: 'So Home says something on a cold start with no signal.',
  },

  /**
   * Name, code, plant. Not clinical, and it is what the header renders — an
   * app that opens to a blank name reads as broken.
   */
  profile: {
    key: 'profile',
    ttlMs: 30 * DAY,
    because: 'Renders the header immediately; contains no clinical content.',
  },
} as const satisfies Record<string, CacheRule>;

export type CacheKey = keyof typeof CACHE_RULES;

/**
 * When an appointment list should stop being offered offline.
 *
 * docs/14: "until the appointment passes + 24h". Past that the cached copy is
 * actively misleading — it shows a worker a slot they have already missed as
 * though it were upcoming.
 */
export function appointmentsExpiry(
  rows: Array<{ scheduledFor?: string | null }>,
  now = Date.now(),
): number {
  const latest = rows.reduce((max, r) => {
    const t = r.scheduledFor ? Date.parse(r.scheduledFor) : NaN;
    return Number.isFinite(t) && t > max ? t : max;
  }, 0);

  // No dated appointment means nothing to keep for; fall back to a day.
  if (!latest) return now + DAY;
  return Math.min(latest + DAY, now + CACHE_RULES.appointments.ttlMs);
}
